import { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Save } from "lucide-react";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  category_slug: "",
  tags: "",
  media_type: "article",
  media_url: "",
  featured: false,
  breaking: false,
  premium: false,
  published: true,
};

export default function AdminEditor() {
  const { user, loading } = useAuth();
  const { slug } = useParams();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    if (slug) {
      api.get(`/articles/${slug}`).then((r) => {
        const a = r.data.article;
        setForm({
          title: a.title,
          excerpt: a.excerpt,
          content: a.content,
          cover_image: a.cover_image,
          category_slug: a.category_slug,
          tags: (a.tags || []).join(", "),
          media_type: a.media_type,
          media_url: a.media_url || "",
          featured: a.featured,
          breaking: a.breaking,
          premium: a.premium || false,
          published: a.published,
        });
      });
    }
  }, [slug]);

  if (loading) return <div className="container-editorial py-24 text-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (slug) {
        await api.put(`/articles/${slug}`, payload);
        toast.success("Story updated.");
      } else {
        const { data } = await api.post("/articles", payload);
        toast.success("Story published.");
        nav(`/admin/edit/${data.slug}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-editorial py-12">
      <Link to="/admin" className="kicker text-[color:var(--text-muted)] flex items-center gap-2 hover:text-[color:var(--accent)]"><ArrowLeft size={14} /> Back to editor desk</Link>
      <h1 className="font-serif text-5xl font-bold mt-4 mb-8 tracking-tight">{slug ? "Edit story" : "New story"}</h1>

      <form onSubmit={submit} className="grid md:grid-cols-3 gap-8" data-testid="editor-form">
        <div className="md:col-span-2 space-y-5">
          <Field label="Headline" required>
            <input required value={form.title} onChange={(e) => set("title", e.target.value)} className="w-full bg-transparent border px-4 py-3 outline-none font-serif text-2xl font-bold" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-title" />
          </Field>
          <Field label="Excerpt / Dek" required>
            <textarea required rows={3} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="w-full bg-transparent border px-4 py-3 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-excerpt" />
          </Field>
          <Field label="Body (double line-break for new paragraph)" required>
            <textarea required rows={20} value={form.content} onChange={(e) => set("content", e.target.value)} className="w-full bg-transparent border px-4 py-3 outline-none font-body leading-relaxed" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-content" />
          </Field>
        </div>

        <aside className="space-y-5">
          <div className="border p-4 bg-[color:var(--surface)]" style={{ borderColor: "hsl(var(--border-hsl))" }}>
            <button disabled={busy} className="btn-accent w-full flex items-center justify-center gap-2 py-4" data-testid="editor-save">
              <Save size={16} /> {busy ? "Saving…" : slug ? "Update Story" : "Publish Story"}
            </button>
          </div>

          <Field label="Cover image" required>
            <div className="flex gap-2 mb-2">
              <label className="btn-outline cursor-pointer flex-1 text-center" data-testid="editor-cover-upload-label">
                <input type="file" accept="image/*" className="hidden" data-testid="editor-cover-upload" onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    setBusy(true);
                    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    const base = process.env.REACT_APP_BACKEND_URL;
                    set("cover_image", `${base}${data.url}`);
                    toast.success("Image uploaded.");
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setBusy(false);
                    e.target.value = "";
                  }
                }} />
                Upload image
              </label>
            </div>
            <input required value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="Or paste image URL" className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-cover" />
            {form.cover_image && <img src={form.cover_image} alt="preview" className="mt-2 w-full aspect-video object-cover" />}
          </Field>

          <Field label="Section">
            <select required value={form.category_slug} onChange={(e) => set("category_slug", e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-category">
              <option value="">Choose a section…</option>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>

          <Field label="Tags (comma separated)">
            <input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="media, essay, opinion" className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-tags" />
          </Field>

          <Field label="Media type">
            <select value={form.media_type} onChange={(e) => set("media_type", e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-media-type">
              <option value="article">Article</option>
              <option value="video">Video</option>
              <option value="audio">Audio</option>
            </select>
          </Field>

          {form.media_type !== "article" && (
            <Field label={form.media_type === "video" ? "YouTube URL" : "Audio URL (mp3)"}>
              <input value={form.media_url} onChange={(e) => set("media_url", e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="editor-media-url" />
            </Field>
          )}

          <div className="border p-4 space-y-3" style={{ borderColor: "hsl(var(--border-hsl))" }}>
            <label className="flex items-center gap-2 kicker">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="editor-featured" />
              Featured on home
            </label>
            <label className="flex items-center gap-2 kicker">
              <input type="checkbox" checked={form.breaking} onChange={(e) => set("breaking", e.target.checked)} data-testid="editor-breaking" />
              Breaking (shown in ticker)
            </label>
            <label className="flex items-center gap-2 kicker">
              <input type="checkbox" checked={form.premium} onChange={(e) => set("premium", e.target.checked)} data-testid="editor-premium" />
              Premium (subscribers only)
            </label>
            <label className="flex items-center gap-2 kicker">
              <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="editor-published" />
              Published (uncheck for draft)
            </label>
          </div>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="kicker block mb-2">{label} {required && <span className="text-[color:var(--accent)]">*</span>}</label>
      {children}
    </div>
  );
}
